import React, { Suspense, useCallback, useContext, useState } from 'react';
import type { CrosscheckCounts } from '@privisy/api-types';
import type { CrosscheckVariant } from '../CrosscheckExplainer';

// Loaded on first open: the report page is in the entry chunk, and the dialog
// is only needed when the reader asks for it.
const CrosscheckExplainer = React.lazy(() => import('../CrosscheckExplainer'));

/**
 * Opens the one "How AI Crosscheck works" dialog (#1658) from anywhere in the
 * report: the banner's link and each disputed card's "What does disputed
 * mean?" link (#1659). Null outside a provider, where callers render no link.
 */
export const CrosscheckExplainerContext = React.createContext<(() => void) | null>(null);

export function useOpenCrosscheckExplainer(): (() => void) | null {
  return useContext(CrosscheckExplainerContext);
}

interface CrosscheckExplainerProviderProps {
  counts?: CrosscheckCounts;
  variant?: CrosscheckVariant;
  children: React.ReactNode;
}

export function CrosscheckExplainerProvider({ counts, variant = 'report', children }: CrosscheckExplainerProviderProps) {
  const [open, setOpen] = useState(false);
  const show = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);

  return (
    <CrosscheckExplainerContext.Provider value={counts ? show : null}>
      {children}
      {open && counts && (
        <Suspense fallback={null}>
          <CrosscheckExplainer counts={counts} variant={variant} onClose={close} />
        </Suspense>
      )}
    </CrosscheckExplainerContext.Provider>
  );
}
